import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { consoleApi, normalizeCapabilities } from '../api/client';
import type { Capabilities, ConsoleUser, SessionResponse } from '../types';
import { advanceSessionGeneration, assertCurrentSessionGeneration, isCurrentSessionGeneration, onSessionExpired, sessionGeneration } from '../lib/session-events';

interface AuthValue {
  capabilities: Capabilities;
  csrfToken: string;
  error: string | null;
  sessionExpired: boolean;
  isLoading: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  user: ConsoleUser | null;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Keep the session observer mounted while retiring every private cache and old read. */
export async function applySession(queryClient: QueryClient, value: SessionResponse, generation: number) {
  assertCurrentSessionGeneration(generation);
  await queryClient.cancelQueries();
  assertCurrentSessionGeneration(generation);
  queryClient.removeQueries({ predicate: query => query.queryKey[0] !== 'session' });
  queryClient.getMutationCache().clear();
  queryClient.setQueryData(['session'], value);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [sessionExpired, setSessionExpired] = useState(false);
  const observedSession = useRef<{ authenticated?: boolean; generation: number }>({ generation: sessionGeneration() });
  const sessionQuery = useQuery({
    queryKey: ['session'],
    queryFn: consoleApi.session,
    retry: false,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true
  });
  const session = sessionQuery.data;
  useEffect(() => onSessionExpired(() => {
    const generation = sessionGeneration();
    setSessionExpired(true);
    void applySession(queryClient, { authenticated: false }, generation).then(() => {
      if (isCurrentSessionGeneration(generation)) return queryClient.invalidateQueries({ queryKey: ['session'] });
    }).catch(() => undefined);
  }), [queryClient]);
  useEffect(() => {
    if (session?.authenticated === false) {
      // A successful anonymous poll also retires requests, without double-bumping
      // after the explicit expiry/login/logout handlers already changed generation.
      if (observedSession.current.authenticated === true && isCurrentSessionGeneration(observedSession.current.generation)) {
        advanceSessionGeneration();
        setSessionExpired(true);
      }
      void queryClient.cancelQueries({ predicate: query => query.queryKey[0] !== 'session' });
      queryClient.removeQueries({ predicate: query => query.queryKey[0] !== 'session' });
      queryClient.getMutationCache().clear();
    }
    observedSession.current = { authenticated: session?.authenticated, generation: sessionGeneration() };
  }, [queryClient, session?.authenticated]);
  const loginMutation = useMutation({
    onMutate: () => ({ generation: sessionGeneration() }),
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      const csrfToken = session?.csrfToken || '';
      return consoleApi.login(username, password, csrfToken);
    },
    onSuccess: async (value, _variables, context) => {
      assertCurrentSessionGeneration(context!.generation);
      const generation = advanceSessionGeneration();
      await applySession(queryClient, value, generation);
      setSessionExpired(false);
    }
  });
  const logoutMutation = useMutation({
    onMutate: () => ({ generation: sessionGeneration() }),
    mutationFn: async () => consoleApi.logout(session?.csrfToken || ''),
    onSuccess: async (_value, _variables, context) => {
      assertCurrentSessionGeneration(context!.generation);
      const generation = advanceSessionGeneration();
      await applySession(queryClient, { authenticated: false }, generation);
      setSessionExpired(false);
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    }
  });

  const error = loginMutation.error instanceof Error
    ? loginMutation.error.message
    : sessionQuery.error instanceof Error
      ? sessionQuery.error.message
      : null;

  return (
    <AuthContext.Provider value={{
      capabilities: normalizeCapabilities(session?.capabilities),
      csrfToken: session?.csrfToken || '',
      error,
      sessionExpired,
      isLoading: sessionQuery.isLoading || (sessionQuery.isFetching && !session?.authenticated && !session?.csrfToken) || loginMutation.isPending || logoutMutation.isPending,
      login: async (username, password) => {
        await loginMutation.mutateAsync({ username, password });
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      user: session?.authenticated && session.user ? session.user : null
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return value;
}
